import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  getAllSubscriptionPlans,
  createSubscriptionPlan,
  updateSubscriptionPlan,
  deleteSubscriptionPlan,
} from '@/lib/superAdmin';
import type { SubscriptionPlan } from '@/lib/subscription';
import { useToast } from '@/hooks/use-toast';
import { Edit2, Trash2, Plus, Save, X, CheckCircle2 } from 'lucide-react';

export function SubscriptionPlansManagement() {
  const { toast } = useToast();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [planToDelete, setPlanToDelete] = useState<SubscriptionPlan | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state for create/edit
  const [formData, setFormData] = useState({
    name: '',
    duration_months: 3,
    price: 0,
    currency: 'INR',
    is_active: true,
    features: {
      support: 'email',
      features: [] as string[]
    }
  });

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    setLoading(true);
    try {
      const data = await getAllSubscriptionPlans();
      setPlans(data);
    } catch (error) {
      console.error('Error loading plans:', error);
      toast({
        title: 'Error',
        description: 'Failed to load subscription plans',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (plan: SubscriptionPlan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      duration_months: plan.duration_months,
      price: plan.price,
      currency: plan.currency,
      is_active: plan.is_active,
      features: plan.features as any || { support: 'email', features: [] }
    });
    setIsCreateDialogOpen(true);
  };

  const handleCreate = () => {
    setEditingPlan(null);
    setFormData({
      name: '',
      duration_months: 3,
      price: 0,
      currency: 'INR',
      is_active: true,
      features: {
        support: 'email',
        features: []
      }
    });
    setIsCreateDialogOpen(true);
  };

  const handleDelete = (plan: SubscriptionPlan) => {
    setPlanToDelete(plan);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!planToDelete) return;

    setSaving(true);
    try {
      const success = await deleteSubscriptionPlan(planToDelete.id);
      if (success) {
        toast({
          title: 'Success',
          description: 'Subscription plan deleted successfully'
        });
        await loadPlans();
      } else {
        throw new Error('Delete failed');
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete subscription plan',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
      setIsDeleteDialogOpen(false);
      setPlanToDelete(null);
    }
  };

  /**
   * Calculate prices for 6-month and 12-month plans based on 3-month plan price
   * Using discount strategy:
   * - 6 months: 7.4% discount
   * - 12 months: 21.3% discount
   */
  const calculateDerivedPrices = (threeMonthPrice: number) => {
    const monthlyRate = threeMonthPrice / 3;
    
    // 6 months: (3-month price / 3) * 6 * (1 - 0.074) = monthlyRate * 6 * 0.926
    const sixMonthPrice = Math.round(monthlyRate * 6 * 0.926);
    
    // 12 months: (3-month price / 3) * 12 * (1 - 0.213) = monthlyRate * 12 * 0.787
    const twelveMonthPrice = Math.round(monthlyRate * 12 * 0.787);
    
    return { sixMonthPrice, twelveMonthPrice };
  };

  const handleSave = async () => {
    if (!formData.name || formData.duration_months <= 0 || formData.price <= 0) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        variant: 'destructive'
      });
      return;
    }

    setSaving(true);
    try {
      if (editingPlan) {
        // Update existing plan
        const updated = await updateSubscriptionPlan(editingPlan.id, formData);
        if (updated) {
          // If 3-month plan was updated, auto-update 6-month and 12-month plans
          const is3MonthPlan = editingPlan.duration_months === 3;
          const priceChanged = Math.abs(formData.price - editingPlan.price) > 0.01; // Handle floating point comparison
          
          console.log('Plan update check:', {
            is3MonthPlan,
            priceChanged,
            oldPrice: editingPlan.price,
            newPrice: formData.price,
            duration: editingPlan.duration_months
          });
          
          if (is3MonthPlan && priceChanged) {
            const { sixMonthPrice, twelveMonthPrice } = calculateDerivedPrices(formData.price);
            
            console.log('Calculated derived prices:', { sixMonthPrice, twelveMonthPrice });
            
            // Get fresh plans data to find 6-month and 12-month plans
            const currentPlans = await getAllSubscriptionPlans();
            const sixMonthPlan = currentPlans.find(p => p.duration_months === 6);
            const twelveMonthPlan = currentPlans.find(p => p.duration_months === 12);
            
            console.log('Found plans:', {
              sixMonthPlan: sixMonthPlan ? { id: sixMonthPlan.id, name: sixMonthPlan.name, currentPrice: sixMonthPlan.price } : null,
              twelveMonthPlan: twelveMonthPlan ? { id: twelveMonthPlan.id, name: twelveMonthPlan.name, currentPrice: twelveMonthPlan.price } : null
            });
            
            if (!sixMonthPlan || !twelveMonthPlan) {
              toast({
                title: 'Warning',
                description: 'Could not find 6-month or 12-month plans to auto-update. Please update them manually.',
                variant: 'destructive'
              });
            } else {
              const updatePromises: Promise<any>[] = [];
              const errors: string[] = [];
              
              // Update 6-month plan
              updatePromises.push(
                updateSubscriptionPlan(sixMonthPlan.id, {
                  price: sixMonthPrice
                }).then((result) => {
                  if (result) {
                    console.log(`✅ Auto-updated 6-month plan from ₹${sixMonthPlan.price} to ₹${sixMonthPrice}`);
                  } else {
                    console.error('❌ Failed to update 6-month plan');
                    errors.push('Failed to update 6-month plan');
                  }
                  return result;
                }).catch((error) => {
                  console.error('Error updating 6-month plan:', error);
                  errors.push('Failed to update 6-month plan');
                  return null;
                })
              );
              
              // Update 12-month plan
              updatePromises.push(
                updateSubscriptionPlan(twelveMonthPlan.id, {
                  price: twelveMonthPrice
                }).then((result) => {
                  if (result) {
                    console.log(`✅ Auto-updated 12-month plan from ₹${twelveMonthPlan.price} to ₹${twelveMonthPrice}`);
                  } else {
                    console.error('❌ Failed to update 12-month plan');
                    errors.push('Failed to update 12-month plan');
                  }
                  return result;
                }).catch((error) => {
                  console.error('Error updating 12-month plan:', error);
                  errors.push('Failed to update 12-month plan');
                  return null;
                })
              );
              
              // Wait for all updates to complete
              const results = await Promise.all(updatePromises);
              console.log('Update results:', results);
              
              if (errors.length > 0) {
                toast({
                  title: 'Partially Updated',
                  description: `3-month plan updated. Some auto-updates failed: ${errors.join(', ')}`,
                  variant: 'destructive'
                });
              } else {
                toast({
                  title: 'Success',
                  description: `Subscription plan updated. 6-month (₹${sixMonthPrice.toLocaleString('en-IN')}) and 12-month (₹${twelveMonthPrice.toLocaleString('en-IN')}) plans auto-updated with discount strategy.`
                });
              }
            }
          } else {
            toast({
              title: 'Success',
              description: 'Subscription plan updated successfully'
            });
          }
          
          await loadPlans();
          setIsCreateDialogOpen(false);
          setEditingPlan(null);
        } else {
          throw new Error('Update failed');
        }
      } else {
        // Create new plan
        const created = await createSubscriptionPlan(formData);
        if (created) {
          toast({
            title: 'Success',
            description: 'Subscription plan created successfully'
          });
          await loadPlans();
          setIsCreateDialogOpen(false);
        } else {
          throw new Error('Create failed');
        }
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save subscription plan',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const addFeature = () => {
    setFormData({
      ...formData,
      features: {
        ...formData.features,
        features: [...formData.features.features, '']
      }
    });
  };

  const updateFeature = (index: number, value: string) => {
    const newFeatures = [...formData.features.features];
    newFeatures[index] = value;
    setFormData({
      ...formData,
      features: {
        ...formData.features,
        features: newFeatures
      }
    });
  };

  const removeFeature = (index: number) => {
    const newFeatures = formData.features.features.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      features: {
        ...formData.features,
        features: newFeatures
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Subscription Plans Management</CardTitle>
                <CardDescription>
                  Manage subscription plans. Changes will sync automatically across all locations.
                  <br />
                  <span className="text-xs text-muted-foreground">
                    💡 Tip: Updating the 3-month plan price will automatically update 6-month and 12-month plans with discount strategy.
                  </span>
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleCreate} className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Create New Plan
                </Button>
              </div>
            </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Plan Name</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Monthly Rate</TableHead>
                  <TableHead>Currency</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Features</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plans.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-muted-foreground">
                      No subscription plans found
                    </TableCell>
                  </TableRow>
                ) : (
                  plans.map((plan) => {
                    const features = plan.features as any;
                    const featureList = features?.features || [];
                    const monthlyRate = Math.round(plan.price / plan.duration_months);

                    return (
                      <TableRow key={plan.id}>
                        <TableCell className="font-medium">{plan.name}</TableCell>
                        <TableCell>{plan.duration_months} months</TableCell>
                        <TableCell>₹{plan.price.toLocaleString('en-IN')}</TableCell>
                        <TableCell>₹{monthlyRate.toLocaleString('en-IN')}/month</TableCell>
                        <TableCell>{plan.currency}</TableCell>
                        <TableCell>
                          <Badge
                            variant={plan.is_active ? 'default' : 'secondary'}
                            className={plan.is_active ? 'bg-green-100 text-green-700' : ''}
                          >
                            {plan.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="max-w-xs">
                            <div className="text-sm text-muted-foreground mb-1">
                              Support: {features?.support || 'N/A'}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {featureList.length} feature(s)
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEdit(plan)}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDelete(plan)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPlan ? 'Edit Subscription Plan' : 'Create New Subscription Plan'}
            </DialogTitle>
            <DialogDescription>
              {editingPlan
                ? 'Update the subscription plan details. Changes will sync everywhere.'
                : 'Create a new subscription plan that will be available to all hospitals.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Plan Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., 3 Months"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="duration">Duration (Months) *</Label>
                <Input
                  id="duration"
                  type="number"
                  min="1"
                  value={formData.duration_months}
                  onChange={(e) =>
                    setFormData({ ...formData, duration_months: parseInt(e.target.value) || 0 })
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="price">Price (INR) *</Label>
                <Input
                  id="price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) =>
                    setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Monthly rate: ₹{formData.duration_months > 0 ? Math.round(formData.price / formData.duration_months).toLocaleString('en-IN') : '0'}/month
                </p>
                {/* Show auto-update preview for 3-month plan */}
                {editingPlan?.duration_months === 3 && formData.price > 0 && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <p className="text-xs font-semibold text-blue-900 mb-2">
                      📊 Auto-update preview (discount strategy will apply):
                    </p>
                    <div className="space-y-1 text-xs text-blue-800">
                      <div className="flex justify-between">
                        <span>6 Months:</span>
                        <span className="font-semibold">₹{calculateDerivedPrices(formData.price).sixMonthPrice.toLocaleString('en-IN')} (7.4% discount)</span>
                      </div>
                      <div className="flex justify-between">
                        <span>12 Months:</span>
                        <span className="font-semibold">₹{calculateDerivedPrices(formData.price).twelveMonthPrice.toLocaleString('en-IN')} (21.3% discount)</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="currency">Currency</Label>
                <Input
                  id="currency"
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="is_active">Active Status</Label>
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, is_active: checked })
                  }
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Inactive plans won't be shown to hospitals
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="support">Support Type</Label>
              <Input
                id="support"
                value={formData.features.support || 'email'}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    features: { ...formData.features, support: e.target.value }
                  })
                }
                placeholder="e.g., email, priority, 24/7"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Features</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={addFeature}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Feature
                </Button>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {formData.features.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      value={feature}
                      onChange={(e) => updateFeature(index, e.target.value)}
                      placeholder="e.g., Patient Registration & Management"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => removeFeature(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                {formData.features.features.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No features added. Click "Add Feature" to add one.
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setIsCreateDialogOpen(false);
                  setEditingPlan(null);
                }}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {editingPlan ? 'Update Plan' : 'Create Plan'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Subscription Plan</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{planToDelete?.name}"? This action cannot be undone.
              <br />
              <br />
              <strong>Note:</strong> You can only delete plans that are not currently in use by any hospitals.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={saving}
              className="bg-red-600 hover:bg-red-700"
            >
              {saving ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

